import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { 
  QuotePricingModel, 
  QuotePricingCreateDto, 
  QuotePricingUpdateDto,
  QuotePricingResponse,
  QuotePricingRegion,
  QuotePricingRegionCreateDto,
  QuotePricingRegionUpdateDto,
  QuotePricingRegionResponse,
  QuotePricingRegionBulkCreateDto,
  QuotePricingRegionBulkUpdateDto
} from "../../modules/shared/model/admin-quote-pricing.interfaces";
import { 
  getQuotePricings as getQuotePricingsAction,
  getQuotePricingById as getQuotePricingByIdAction,
  createQuotePricingAction,
  updateQuotePricingAction,
  deleteQuotePricingAction,
  getQuotePricingRegions as getQuotePricingRegionsAction,
  getQuotePricingRegionById as getQuotePricingRegionByIdAction,
  createQuotePricingRegionAction,
  updateQuotePricingRegionAction,
  deleteQuotePricingRegionAction,
  bulkCreateQuotePricingRegionsAction,
  bulkUpdateQuotePricingRegionsAction
} from "../Actions/AdminQuotePricingActions";

// ===== QUOTE PRICINGS ASYNC THUNKS =====

export const fetchQuotePricings = createAsyncThunk("adminQuotePricings/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getQuotePricingsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch quote pricings"); 
  }
});

export const fetchQuotePricingById = createAsyncThunk("adminQuotePricings/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getQuotePricingByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch quote pricing"); 
  }
});

export const createQuotePricing = createAsyncThunk("adminQuotePricings/create", async (data: QuotePricingCreateDto, { rejectWithValue }) => {
  try { 
    return await createQuotePricingAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create quote pricing"); 
  }
});

export const updateQuotePricing = createAsyncThunk("adminQuotePricings/update", async ({ id, data }: { id: string; data: QuotePricingUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateQuotePricingAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update quote pricing"); 
  }
});

export const deleteQuotePricing = createAsyncThunk("adminQuotePricings/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteQuotePricingAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete quote pricing"); 
  }
});

// ===== QUOTE PRICING REGIONS ASYNC THUNKS =====

export const fetchQuotePricingRegions = createAsyncThunk("adminQuotePricingRegions/fetchAll", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { 
    return await getQuotePricingRegionsAction(query); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch quote pricing regions"); 
  }
});

export const fetchQuotePricingRegionById = createAsyncThunk("adminQuotePricingRegions/fetchById", async (id: string, { rejectWithValue }) => {
  try { 
    return await getQuotePricingRegionByIdAction(id); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to fetch quote pricing region"); 
  }
});

export const createQuotePricingRegion = createAsyncThunk("adminQuotePricingRegions/create", async (data: QuotePricingRegionCreateDto, { rejectWithValue }) => {
  try { 
    return await createQuotePricingRegionAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to create quote pricing region"); 
  }
});

export const updateQuotePricingRegion = createAsyncThunk("adminQuotePricingRegions/update", async ({ id, data }: { id: string; data: QuotePricingRegionUpdateDto }, { rejectWithValue }) => {
  try { 
    return await updateQuotePricingRegionAction(id, data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to update quote pricing region"); 
  }
});

export const deleteQuotePricingRegion = createAsyncThunk("adminQuotePricingRegions/delete", async (id: string, { rejectWithValue }) => {
  try { 
    await deleteQuotePricingRegionAction(id); 
    return id; 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to delete quote pricing region"); 
  }
});

// ===== BULK OPERATIONS ASYNC THUNKS =====

export const bulkCreateQuotePricingRegions = createAsyncThunk("adminQuotePricingRegions/bulkCreate", async (data: QuotePricingRegionBulkCreateDto, { rejectWithValue }) => {
  try { 
    return await bulkCreateQuotePricingRegionsAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk create quote pricing regions"); 
  }
});

export const bulkUpdateQuotePricingRegions = createAsyncThunk("adminQuotePricingRegions/bulkUpdate", async (data: QuotePricingRegionBulkUpdateDto, { rejectWithValue }) => {
  try { 
    return await bulkUpdateQuotePricingRegionsAction(data); 
  } catch (e: any) { 
    return rejectWithValue(e?.message || "Failed to bulk update quote pricing regions"); 
  }
});

// ===== STATE INTERFACE =====

interface AdminQuotePricingState {
  // Quote Pricings
  quotePricings: QuotePricingModel[];
  currentQuotePricing: QuotePricingModel | null;
  quotePricingsLoading: boolean;
  quotePricingsError: string | null;
  quotePricingsTotalCount?: number;
  quotePricingsCurrentPage: number;
  quotePricingsHasNextPage: boolean;
  quotePricingsHasPreviousPage: boolean;
  quotePricingsSearchQuery: string;
  
  // Quote Pricing Regions
  quotePricingRegions: QuotePricingRegion[];
  currentQuotePricingRegion: QuotePricingRegion | null;
  quotePricingRegionsLoading: boolean;
  quotePricingRegionsError: string | null;
  quotePricingRegionsTotalCount?: number;
  quotePricingRegionsCurrentPage: number;
  quotePricingRegionsHasNextPage: boolean;
  quotePricingRegionsHasPreviousPage: boolean;
  quotePricingRegionsSearchQuery: string;
  
  // Bulk Operations
  bulkOperationsLoading: boolean;
  bulkOperationsError: string | null;
  bulkOperationsResult: {
    success: any[];
    errors: any[];
    total: number;
    successCount: number;
    errorCount: number;
  } | null;
  
  // Form state
  isFormDirty: boolean;
  lastSavedAt: string | null;
}

const initialState: AdminQuotePricingState = {
  // Quote Pricings
  quotePricings: [],
  currentQuotePricing: null,
  quotePricingsLoading: false,
  quotePricingsError: null,
  quotePricingsTotalCount: 0,
  quotePricingsCurrentPage: 1,
  quotePricingsHasNextPage: false,
  quotePricingsHasPreviousPage: false,
  quotePricingsSearchQuery: "",
  
  // Quote Pricing Regions
  quotePricingRegions: [],
  currentQuotePricingRegion: null,
  quotePricingRegionsLoading: false,
  quotePricingRegionsError: null,
  quotePricingRegionsTotalCount: 0,
  quotePricingRegionsCurrentPage: 1,
  quotePricingRegionsHasNextPage: false,
  quotePricingRegionsHasPreviousPage: false,
  quotePricingRegionsSearchQuery: "",
  
  // Bulk Operations
  bulkOperationsLoading: false,
  bulkOperationsError: null,
  bulkOperationsResult: null,
  
  // Form state
  isFormDirty: false,
  lastSavedAt: null,
};

const adminQuotePricingSlice = createSlice({
  name: "adminQuotePricings",
  initialState,
  reducers: {
    // Quote Pricings Reducers
    clearQuotePricingsError(state) { 
      state.quotePricingsError = null; 
    },
    clearCurrentQuotePricing(state) { 
      state.currentQuotePricing = null; 
    },
    setQuotePricingsSearchQuery(state, action: PayloadAction<string>) { 
      state.quotePricingsSearchQuery = action.payload; 
    },
    
    // Quote Pricing Regions Reducers
    clearQuotePricingRegionsError(state) { 
      state.quotePricingRegionsError = null; 
    },
    clearCurrentQuotePricingRegion(state) { 
      state.currentQuotePricingRegion = null; 
    },
    setQuotePricingRegionsSearchQuery(state, action: PayloadAction<string>) { 
      state.quotePricingRegionsSearchQuery = action.payload; 
    },
    
    // Bulk Operations Reducers
    clearBulkOperationsError(state) { 
      state.bulkOperationsError = null; 
    },
    clearBulkOperationsResult(state) { 
      state.bulkOperationsResult = null; 
    },
    
    // Form Reducers
    setQuotePricingFormDirty(state, action: PayloadAction<boolean>) { 
      state.isFormDirty = action.payload; 
    },
    clearAllQuotePricings(state) {
      state.quotePricings = [];
      state.quotePricingRegions = [];
      state.currentQuotePricing = null;
      state.currentQuotePricingRegion = null;
      state.quotePricingsTotalCount = 0;
      state.quotePricingRegionsTotalCount = 0;
    },
    updateQuotePricingFromForm(state, action: PayloadAction<Partial<QuotePricingModel>>) {
      if (state.currentQuotePricing) {
        state.currentQuotePricing = { ...state.currentQuotePricing, ...action.payload };
      }
    },
    updateQuotePricingRegionFromForm(state, action: PayloadAction<Partial<QuotePricingRegion>>) {
      if (state.currentQuotePricingRegion) {
        state.currentQuotePricingRegion = { ...state.currentQuotePricingRegion, ...action.payload };
      }
    }
  },
  extraReducers: (builder) => {
    // ===== QUOTE PRICINGS EXTRA REDUCERS =====
    
    // Fetch Quote Pricings
    builder
      .addCase(fetchQuotePricings.pending, (state) => { 
        state.quotePricingsLoading = true; 
        state.quotePricingsError = null; 
      })
      .addCase(fetchQuotePricings.fulfilled, (state, action: PayloadAction<QuotePricingResponse>) => { 
        console.log("Redux: fetchQuotePricings.fulfilled", action.payload);
        state.quotePricingsLoading = false; 
        state.quotePricings = action.payload.data || []; 
        state.quotePricingsTotalCount = action.payload.totalCount || 0; 
        state.quotePricingsCurrentPage = action.payload.page || 1;
        state.quotePricingsHasNextPage = action.payload.hasNextPage || false;
        state.quotePricingsHasPreviousPage = action.payload.hasPreviousPage || false;
        console.log("Redux: Updated state", {
          quotePricings: state.quotePricings,
          totalCount: state.quotePricingsTotalCount,
          hasNextPage: state.quotePricingsHasNextPage
        });
      })
      .addCase(fetchQuotePricings.rejected, (state, action: any) => { 
        state.quotePricingsLoading = false; 
        state.quotePricingsError = action.payload as string; 
      });

    // Fetch Quote Pricing by ID
    builder
      .addCase(fetchQuotePricingById.pending, (state) => { 
        state.quotePricingsLoading = true; 
        state.quotePricingsError = null; 
      })
      .addCase(fetchQuotePricingById.fulfilled, (state, action: PayloadAction<QuotePricingModel>) => { 
        state.quotePricingsLoading = false; 
        state.currentQuotePricing = action.payload; 
      })
      .addCase(fetchQuotePricingById.rejected, (state, action: any) => { 
        state.quotePricingsLoading = false; 
        state.quotePricingsError = action.payload as string; 
      });

    // Create Quote Pricing
    builder
      .addCase(createQuotePricing.pending, (state) => { 
        state.quotePricingsLoading = true; 
        state.quotePricingsError = null; 
      })
      .addCase(createQuotePricing.fulfilled, (state, action: PayloadAction<QuotePricingModel>) => { 
        state.quotePricingsLoading = false; 
        state.quotePricings.unshift(action.payload); 
        state.quotePricingsTotalCount = (state.quotePricingsTotalCount || 0) + 1; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createQuotePricing.rejected, (state, action: any) => { 
        state.quotePricingsLoading = false; 
        state.quotePricingsError = action.payload as string; 
      });

    // Update Quote Pricing
    builder
      .addCase(updateQuotePricing.pending, (state) => { 
        state.quotePricingsLoading = true; 
        state.quotePricingsError = null; 
      })
      .addCase(updateQuotePricing.fulfilled, (state, action: PayloadAction<QuotePricingModel>) => {
        state.quotePricingsLoading = false;
        const index = state.quotePricings.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.quotePricings[index] = action.payload;
        if (state.currentQuotePricing?.id === action.payload.id) state.currentQuotePricing = action.payload;
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateQuotePricing.rejected, (state, action: any) => { 
        state.quotePricingsLoading = false; 
        state.quotePricingsError = action.payload as string; 
      });

    // Delete Quote Pricing
    builder
      .addCase(deleteQuotePricing.pending, (state) => { 
        state.quotePricingsLoading = true; 
        state.quotePricingsError = null; 
      })
      .addCase(deleteQuotePricing.fulfilled, (state, action: PayloadAction<string>) => { 
        state.quotePricingsLoading = false; 
        state.quotePricings = state.quotePricings.filter(x => x.id !== action.payload); 
        if (state.currentQuotePricing?.id === action.payload) state.currentQuotePricing = null; 
        state.quotePricingsTotalCount = Math.max(0, (state.quotePricingsTotalCount || 0) - 1); 
      })
      .addCase(deleteQuotePricing.rejected, (state, action: any) => { 
        state.quotePricingsLoading = false; 
        state.quotePricingsError = action.payload as string; 
      });

    // ===== QUOTE PRICING REGIONS EXTRA REDUCERS =====
    
    // Fetch Quote Pricing Regions
    builder
      .addCase(fetchQuotePricingRegions.pending, (state) => { 
        state.quotePricingRegionsLoading = true; 
        state.quotePricingRegionsError = null; 
      })
      .addCase(fetchQuotePricingRegions.fulfilled, (state, action: PayloadAction<QuotePricingRegionResponse>) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegions = action.payload.data || []; 
        state.quotePricingRegionsTotalCount = action.payload.totalCount || 0; 
        state.quotePricingRegionsCurrentPage = action.payload.page || 1;
        state.quotePricingRegionsHasNextPage = action.payload.hasNextPage || false;
        state.quotePricingRegionsHasPreviousPage = action.payload.hasPreviousPage || false;
      })
      .addCase(fetchQuotePricingRegions.rejected, (state, action: any) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegionsError = action.payload as string; 
      });

    // Fetch Quote Pricing Region by ID
    builder
      .addCase(fetchQuotePricingRegionById.pending, (state) => { 
        state.quotePricingRegionsLoading = true; 
        state.quotePricingRegionsError = null; 
      })
      .addCase(fetchQuotePricingRegionById.fulfilled, (state, action: PayloadAction<QuotePricingRegion>) => { 
        state.quotePricingRegionsLoading = false; 
        state.currentQuotePricingRegion = action.payload; 
      })
      .addCase(fetchQuotePricingRegionById.rejected, (state, action: any) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegionsError = action.payload as string; 
      });

    // Create Quote Pricing Region
    builder
      .addCase(createQuotePricingRegion.pending, (state) => { 
        state.quotePricingRegionsLoading = true; 
        state.quotePricingRegionsError = null; 
      })
      .addCase(createQuotePricingRegion.fulfilled, (state, action: PayloadAction<QuotePricingRegion>) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegions.unshift(action.payload); 
        state.quotePricingRegionsTotalCount = (state.quotePricingRegionsTotalCount || 0) + 1; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(createQuotePricingRegion.rejected, (state, action: any) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegionsError = action.payload as string; 
      });

    // Update Quote Pricing Region
    builder
      .addCase(updateQuotePricingRegion.pending, (state) => { 
        state.quotePricingRegionsLoading = true; 
        state.quotePricingRegionsError = null; 
      })
      .addCase(updateQuotePricingRegion.fulfilled, (state, action: PayloadAction<QuotePricingRegion>) => {
        state.quotePricingRegionsLoading = false;
        const index = state.quotePricingRegions.findIndex(x => x.id === action.payload.id);
        if (index !== -1) state.quotePricingRegions[index] = action.payload;
        if (state.currentQuotePricingRegion?.id === action.payload.id) state.currentQuotePricingRegion = action.payload;
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(updateQuotePricingRegion.rejected, (state, action: any) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegionsError = action.payload as string; 
      });

    // Delete Quote Pricing Region
    builder
      .addCase(deleteQuotePricingRegion.pending, (state) => { 
        state.quotePricingRegionsLoading = true; 
        state.quotePricingRegionsError = null; 
      })
      .addCase(deleteQuotePricingRegion.fulfilled, (state, action: PayloadAction<string>) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegions = state.quotePricingRegions.filter(x => x.id !== action.payload); 
        if (state.currentQuotePricingRegion?.id === action.payload) state.currentQuotePricingRegion = null; 
        state.quotePricingRegionsTotalCount = Math.max(0, (state.quotePricingRegionsTotalCount || 0) - 1); 
      })
      .addCase(deleteQuotePricingRegion.rejected, (state, action: any) => { 
        state.quotePricingRegionsLoading = false; 
        state.quotePricingRegionsError = action.payload as string; 
      });

    // ===== BULK OPERATIONS EXTRA REDUCERS =====
    
    // Bulk Create Quote Pricing Regions
    builder
      .addCase(bulkCreateQuotePricingRegions.pending, (state) => { 
        state.bulkOperationsLoading = true; 
        state.bulkOperationsError = null; 
      })
      .addCase(bulkCreateQuotePricingRegions.fulfilled, (state, action: any) => { 
        state.bulkOperationsLoading = false; 
        state.bulkOperationsResult = action.payload;
        state.quotePricingRegions.unshift(...action.payload.success); 
        state.quotePricingRegionsTotalCount = (state.quotePricingRegionsTotalCount || 0) + action.payload.successCount; 
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkCreateQuotePricingRegions.rejected, (state, action: any) => { 
        state.bulkOperationsLoading = false; 
        state.bulkOperationsError = action.payload as string; 
      });

    // Bulk Update Quote Pricing Regions
    builder
      .addCase(bulkUpdateQuotePricingRegions.pending, (state) => { 
        state.bulkOperationsLoading = true; 
        state.bulkOperationsError = null; 
      })
      .addCase(bulkUpdateQuotePricingRegions.fulfilled, (state, action: any) => {
        state.bulkOperationsLoading = false;
        state.bulkOperationsResult = action.payload;
        action.payload.success.forEach((updatedRegion: QuotePricingRegion) => {
          const index = state.quotePricingRegions.findIndex(x => x.id === updatedRegion.id);
          if (index !== -1) state.quotePricingRegions[index] = updatedRegion;
          if (state.currentQuotePricingRegion?.id === updatedRegion.id) state.currentQuotePricingRegion = updatedRegion;
        });
        state.lastSavedAt = new Date().toISOString();
        state.isFormDirty = false;
      })
      .addCase(bulkUpdateQuotePricingRegions.rejected, (state, action: any) => { 
        state.bulkOperationsLoading = false; 
        state.bulkOperationsError = action.payload as string; 
      });
  }
});

export const { 
  clearQuotePricingsError,
  clearCurrentQuotePricing,
  setQuotePricingsSearchQuery,
  clearQuotePricingRegionsError,
  clearCurrentQuotePricingRegion,
  setQuotePricingRegionsSearchQuery,
  clearBulkOperationsError,
  clearBulkOperationsResult,
  setQuotePricingFormDirty,
  clearAllQuotePricings,
  updateQuotePricingFromForm,
  updateQuotePricingRegionFromForm
} = adminQuotePricingSlice.actions;

export default adminQuotePricingSlice.reducer;
